// A/B Tests: did the experiment move the funnel?
//
// The sibling of Version Cohorts, across a different boundary. That page asks
// "did the last RELEASE move the numbers?"; this one asks "did this EXPERIMENT
// move them?", over the same population and the same step definitions, so the
// two pages cannot disagree about what "searched" or "picked" means. Both read
// a worker-materialized payload (`GET /api/admin/ab-tests`), so every figure on
// screen was measured in one pass, at one instant.
//
// Four things about the design are deliberate, and two of them are the same
// promises Version Cohorts makes:
//
// - **The population is who MET the experiment, not who hashes into an arm.**
//   Both arms are read from `experiment_exposures` (migration 0188), written at
//   the point both arms are still the same event. Membership used to be a sha256
//   over the user id compared against the dial, which classifies everyone who
//   ever signed up: a ~139k denominator against a numerator that could only
//   start when the dial moved, diluting every rate here by an amount the page
//   never disclosed. Expect these arms to be small. That is the fix, not a bug.
// - **An unmeasured side reads "-", never 0%.** Null is unknown. Rendering it as
//   zero turns "nobody is in this arm" into "this arm converts at nothing",
//   which is the most expensive misread available here — and, while both dials
//   sit at 0%, it is the reading a careless page would produce on every row.
// - **Every number says how much evidence it rests on.** Each arm's population
//   sits under its heading, and a thin arm is called out in words above the
//   table rather than left for the reader to infer from a suspiciously round
//   percentage.
// - **The delta is in POINTS, not percent.** The reader is comparing two rates:
//   "8 points" is a statement about the funnel, while "40% better" invites them
//   to multiply it by something.
//
// Since the experimentation migration this is the OUTCOMES TAB of one
// experiment's page (`/experiments/$key`, `ExperimentOutcomes`), not a page of
// its own: the experiment is fixed by the route, and the old `/ab-tests` path
// redirects there. The window and exposure-date range are held locally.
//
// TWO TABLES, and the split is the point. The SHARED funnel steps are the same
// seven rungs on every experiment's page, so two experiments can be compared with
// each other; the KPI table under them is this experiment's OWN measurements
// (`recruiter.experiments.ExperimentSpec.kpis`) and is meaningless on any other.
// An experiment picks its page, never its step definitions.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AbAmountRow,
  AbCannibalization,
  AbCell,
  AbPresetWindow,
  AbStepRow,
  AdminAbTestsResponse,
} from "@recruiter/shared/types";
import { DEFAULT_ARMS, armLabel } from "../lib/experiment-scope";
import {
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  isDateRangeActive,
  TableExportButton,
  type DateRange,
} from "@recruiter/shared";

import { AreaBoundary } from "../components/area-gate";
import { CohortTabs } from "../components/cohort-tabs";
import { EmptyState, LoadingState } from "../components/states";
import { dateRangeKey } from "../lib/date-range";
import { formatDateTime } from "../lib/format";
import { abTestsQuery, abTestsRangeQuery, adminKeys, funnelRangeGaveUp } from "../lib/queries";
import "./ab-tests.css";

/** The windows, in the order the picker renders them. `all` first: while a dial
 *  sits at 0 it is the only one with enough of an experiment arm to show
 *  anything at all, so it is the honest default rather than a fallback. */
const WINDOWS: readonly { key: AbPresetWindow; label: string }[] = [
  // horizon-allow: a user-chosen window, not a claim about the data
  { key: "all", label: "All time" },
  { key: "90d", label: "Last 90 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "7d", label: "Last 7 days" },
];

/** Below this, an arm is too thin to read as a result. Not a significance test
 *  — this page does not claim to run one — just the point past which a rate
 *  stops being noise-dominated enough to put in front of somebody without a
 *  sentence attached. */
const THIN_ARM = 30;

function pct(rate: number | null | undefined): string {
  // `null` is UNKNOWN and gets an em-dash-free placeholder; 0 is a real answer
  // and prints as one. The two must never render the same.
  return rate === null || rate === undefined ? "-" : `${(rate * 100).toFixed(1)}%`;
}

function points(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return "-";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)} pts`;
}

/** An estimated headcount. One decimal, because it is a rate times a count and
 *  printing it as a whole number would dress an estimate up as a census. */
function people(n: number | null | undefined): string {
  return n === null || n === undefined ? "-" : n.toFixed(1);
}

function deltaClass(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return "ab-tests__delta";
  if (Math.abs(delta) < 0.05) return "ab-tests__delta";
  // Up the funnel is better everywhere on this page: every step is something a
  // client DID, so more of it is the win. No per-metric direction to carry.
  return delta > 0
    ? "ab-tests__delta ab-tests__delta--up"
    : "ab-tests__delta ab-tests__delta--down";
}

/** The did-it-cost-us panel: of the clients who engaged and left the brief
 *  unfinished, how many would never have finished, and how many we LOST.
 *
 *  The one number on this page that is not a measurement, and it is labelled as
 *  such in three places rather than one, because a derived figure printed beside
 *  measured ones gets read as measured:
 *   - the heading says "estimated";
 *   - the assumption it rests on is spelled out under the split, not linked to;
 *   - the assumption-free cross-check sits in the same panel, so a reader who
 *     distrusts the estimate has the observed number without leaving.
 *
 *  Experiment arm only. Control has no rail, so its engagement is 0 by
 *  construction; printing that as a comparison would read as a collapse.
 */
function CannibalizationPanel({ data }: { data: AbCannibalization }) {
  // Keyed on the ESTIMATE being absent, not on one of its inputs: the route
  // refuses to compute for two different reasons and both must surface, and
  // reading one input would let the other slip through as a confident 0.0.
  const unknown = data.wouldHaveFinished === null;
  const noBaseRate = data.controlCompletionRate === null;
  return (
    <section className="ab-tests__section">
      <h2 className="ab-tests__section-title">
        Estimated cost: briefs this experiment may have taken
      </h2>
      <p className="ab-tests__section-sub">
        Of the <strong>{data.engagedUnfinished.toLocaleString()}</strong> clients in the experiment
        arm who {data.engagedLabel.toLowerCase()} and never {data.completedLabel.toLowerCase()}, how
        many would have finished anyway? Counting them alone says nothing: most were never going to
        finish. Only the split is a result. Control arm only appears here as the base rate, because
        it has no rail to engage with.
      </p>
      {unknown ? (
        <p className="ab-tests__warn">
          {noBaseRate
            ? "The control arm has no clients who started a brief, so there is no base rate to compare against."
            : "No client in the experiment arm has started a brief, so there is nothing here to estimate about."}{" "}
          The split below reads <strong>-</strong> rather than 0: an estimate nobody can compute is
          not an estimate that the experiment cost nothing. This is today's state at a 0% dial, not
          a finding.
        </p>
      ) : null}
      <div className="ab-tests__cannib">
        <div className="ab-tests__cannib-cell">
          <span className="ab-tests__cannib-label">Engaged, brief unfinished</span>
          <span className="ab-tests__cannib-value">{data.engagedUnfinished.toLocaleString()}</span>
          <span className="ab-tests__cannib-sub">measured, experiment arm</span>
        </div>
        <div className="ab-tests__cannib-cell">
          <span className="ab-tests__cannib-label">Would not have finished anyway</span>
          <span className="ab-tests__cannib-value">{people(data.wouldNotHaveFinished)}</span>
          <span className="ab-tests__cannib-sub">no loss</span>
        </div>
        <div className="ab-tests__cannib-cell ab-tests__cannib-cell--cost">
          <span className="ab-tests__cannib-label">Would have finished</span>
          <span className="ab-tests__cannib-value">{people(data.wouldHaveFinished)}</span>
          <span className="ab-tests__cannib-sub">the briefs it may have cost</span>
        </div>
      </div>
      <p className="ab-tests__cannib-note">
        <strong>Estimated, not measured.</strong> The split is the measured count times the control
        arm's completion rate ({pct(data.controlCompletionRate)} of its brief-starters finished,
        against {pct(data.experimentCompletionRate)} in the experiment arm). It assumes the clients
        who engaged were, in how likely they were to finish a brief, like brief-starters generally.
        Nothing here can check that.
      </p>
      <p className="ab-tests__cannib-note">
        <strong>Cross-check, which assumes nothing:</strong> the arms differ by{" "}
        <span className={deltaClass(data.gapPoints === null ? null : -data.gapPoints)}>
          {points(data.gapPoints)}
        </span>{" "}
        of completion, which over the experiment arm's brief-starters is {people(data.gapClients)}{" "}
        clients. That is the experiment's actual effect on finished briefs. If it and the estimate
        above disagree, believe this one and treat the estimate's assumption as broken.
      </p>
    </section>
  );
}

/** One table of measured rows. Used twice — the shared funnel steps and the
 *  selected experiment's own KPIs — because the two must render by identical
 *  rules: a null rate is a dash, a count sits under its rate, and the gap is in
 *  points. Two hand-written tables would drift on exactly those three. */
function RowTable({
  caption,
  rows,
  arms,
}: {
  caption: string;
  rows: readonly AbStepRow[];
  arms: readonly string[];
}) {
  const baseline = arms[0];
  // One cell per ARM from the row's `arms` map (N arms, baseline first), with
  // the flat two-arm fields as the fallback for a readout written before the
  // map existed. The delta column is per non-baseline arm, against the baseline.
  const cell = (row: AbStepRow, arm: string): AbCell =>
    row.arms?.[arm] ??
    (arm === baseline
      ? { count: row.controlCount, rate: row.controlRate, delta: null }
      : { count: row.experimentCount, rate: row.experimentRate, delta: row.deltaPoints });
  return (
    <table className="ab-tests__table">
      <caption className="ab-tests__caption rc-table-export-host">
        <TableExportButton name="ab-test-funnel" label="the funnel comparison" />
        {caption}
      </caption>
      <thead>
        <tr>
          <th scope="col">Step</th>
          {arms.map((arm) => (
            <th key={arm} scope="col">
              {armLabel(arm, arms)}
            </th>
          ))}
          <th scope="col">{arms.length > 2 ? "Difference to A" : "Difference"}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">{row.label}</th>
            {arms.map((arm) => {
              const c = cell(row, arm);
              return (
                <td key={arm}>
                  <span className="ab-tests__rate">{pct(c.rate)}</span>
                  <span className="ab-tests__count">{c.count.toLocaleString()}</span>
                </td>
              );
            })}
            <td>
              {arms
                .filter((arm) => arm !== baseline)
                .map((arm) => {
                  const c = cell(row, arm);
                  return (
                    <span key={arm} className={deltaClass(c.delta)}>
                      {arms.length > 2 ? `${armLabel(arm, arms).slice(-3)} ` : ""}
                      {points(c.delta)}
                    </span>
                  );
                })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** What each arm CONSUMED: totals and the per-user mean beside them.
 *
 *  Its own table rather than a third `RowTable`, because these are quantities,
 *  not rates, and the difference is load-bearing on this page. A rate has a
 *  natural denominator (the arm) and a natural ceiling (100%); a spend has
 *  neither, so it is shown as a total AND a per-user mean, and the gap between
 *  the arms is stated in the row's own unit rather than in points. Reusing
 *  `RowTable` here would have meant printing dollars in a column headed by a
 *  percentage.
 *
 *  Per-user is the honest comparison and reads first: at an uneven dial the arms
 *  hold different numbers of people, so their TOTALS are not comparable at all,
 *  and a reader who compares them anyway is measuring the dial. The total is
 *  still shown, dimmed beneath, because "what did this actually cost us" is a
 *  real question and it is a total.
 */
function AmountTable({ rows, arms }: { rows: readonly AbAmountRow[]; arms: readonly string[] }) {
  const baseline = arms[0];
  const cell = (row: AbAmountRow, arm: string): AbCell =>
    row.arms?.[arm] ??
    (arm === baseline
      ? { count: row.controlTotal, rate: row.controlPerUser, delta: null }
      : { count: row.experimentTotal, rate: row.experimentPerUser, delta: row.deltaPerUser });
  const fmt = (value: number | null | undefined, unit: AbAmountRow["unit"]): string => {
    if (value === null || value === undefined) return "-";
    if (unit !== "usd") {
      // Counts read to one decimal ONLY as a per-user mean; a total is whole.
      return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
    }
    // Sub-cent per-user spend is the normal case early on, and rounding it to
    // $0.00 would report the experiment as free. Widen rather than round away.
    const digits = value !== 0 && Math.abs(value) < 0.01 ? 4 : 2;
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}`;
  };
  const deltaText = (delta: number | null | undefined, unit: AbAmountRow["unit"]): string => {
    if (delta === null || delta === undefined) return "-";
    const sign = delta > 0 ? "+" : "";
    return `${sign}${fmt(delta, unit)}`;
  };
  return (
    <table className="ab-tests__table">
      <caption className="ab-tests__caption rc-table-export-host">
        <TableExportButton name="ab-test-measures" label="the measure comparison" />
        Per user first, because the arms hold different numbers of people: their totals are only
        comparable when the dial is 50/50. Totals are underneath.
      </caption>
      <thead>
        <tr>
          <th scope="col">Measure</th>
          {arms.map((arm) => (
            <th key={arm} scope="col">
              {armLabel(arm, arms)}
            </th>
          ))}
          <th scope="col">Difference per user</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">{row.label}</th>
            {arms.map((arm) => {
              const c = cell(row, arm);
              return (
                <td key={arm}>
                  <span className="ab-tests__rate">{fmt(c.rate, row.unit)}</span>
                  <span className="ab-tests__count">{fmt(c.count, row.unit)} total</span>
                </td>
              );
            })}
            {/* NO up/down colouring here, unlike the step rows. On the funnel more
                is better everywhere; on SPEND more is worse and on searches it is
                neither, so a green "+" would be an opinion the page has not
                earned. */}
            <td className="ab-tests__delta">
              {arms
                .filter((arm) => arm !== baseline)
                .map((arm) => (
                  <span key={arm}>
                    {arms.length > 2 ? `${armLabel(arm, arms).slice(-3)} ` : ""}
                    {deltaText(cell(row, arm).delta, row.unit)}{" "}
                  </span>
                ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The same rows as a chart: two bars per row, control above experiment.
 *
 * Deliberately plain. It draws the SAME numbers the table beside it does and
 * adds one thing a table cannot — the shape of the drop from one rung to the
 * next, which is what somebody reading a funnel is actually looking for.
 *
 * Three honesty rules, the page's own, carried into the geometry:
 *  - **An unknown rate draws NO bar** and is labelled "-". A zero-width bar and
 *    a zero rate look identical, and this page's whole thesis is that they are
 *    not the same thing.
 *  - **Bars are scaled to 100%, not to the biggest value.** Auto-scaling makes a
 *    3%-vs-4% pair look like a rout. The axis is the share of the arm, so the
 *    axis is the arm.
 *  - **Rows keep table order** (widest rung first), so the eye reads a funnel
 *    top-down rather than a ranking.
 */
function ArmBars({
  rows,
  label,
  arms,
}: {
  rows: readonly AbStepRow[];
  label: string;
  arms: readonly string[];
}) {
  const baseline = arms[0];
  const BAR_H = 11;
  const GAP = 3;
  // One bar per ARM, baseline first, so the row grows with the arm count.
  const ROW_H = arms.length * (BAR_H + GAP) + 9;
  const LABEL_W = 190;
  const WIDTH = 620;
  const track = WIDTH - LABEL_W - 56;
  const height = rows.length * ROW_H + 8;
  const rateOf = (row: AbStepRow, arm: string): number | null | undefined =>
    row.arms?.[arm]?.rate ?? (arm === baseline ? row.controlRate : row.experimentRate);
  const deltaOf = (row: AbStepRow, arm: string): number | null | undefined =>
    row.arms?.[arm]?.delta ?? (arm === baseline ? null : row.deltaPoints);
  return (
    <svg
      className="ab-tests__chart"
      viewBox={`0 0 ${WIDTH} ${height}`}
      role="img"
      aria-label={label}
    >
      {rows.map((row, i) => {
        const y = i * ROW_H + 4;
        const mid = y + (arms.length * (BAR_H + GAP) - GAP) / 2 + 4;
        return (
          <g key={row.key}>
            <text className="ab-tests__chart-label" x={LABEL_W - 8} y={mid}>
              {row.label}
            </text>
            <rect
              className="ab-tests__chart-track"
              x={LABEL_W}
              y={y}
              width={track}
              height={arms.length * (BAR_H + GAP) - GAP}
              rx={2}
            />
            {arms.map((arm, a) => {
              const rate = rateOf(row, arm);
              return rate === null || rate === undefined ? null : (
                <rect
                  key={arm}
                  className={`ab-tests__chart-bar ab-tests__chart-bar--${armSuffix(a)}`}
                  x={LABEL_W}
                  y={y + a * (BAR_H + GAP)}
                  width={Math.max(1, rate * track)}
                  height={BAR_H}
                  rx={2}
                />
              );
            })}
            {arms
              .filter((arm) => arm !== baseline)
              .map((arm, a) => (
                <text
                  key={arm}
                  className="ab-tests__chart-value"
                  x={LABEL_W + track + 8}
                  y={y + (a + 1) * (BAR_H + GAP) + BAR_H - 2}
                >
                  {points(deltaOf(row, arm))}
                </text>
              ))}
          </g>
        );
      })}
    </svg>
  );
}

/** The CSS modifier for the arm at `index`: `a` for the baseline, `b`, `c`, `d`
 *  for the rest, wrapping past the palette rather than styling nothing. */
function armSuffix(index: number): string {
  return "abcd"[index % 4] ?? "a";
}

/** The page heading and its cohort controls.
 *
 *  Its own component because it is rendered above EVERY state the page can be
 *  in, including "still building". A range that is computing must stay
 *  escapable: an operator who picked dates the worker is still chewing on (or
 *  dates with nothing in them) would otherwise be stranded on a status screen
 *  with no way back to a preset except editing the URL. */
function AbControls({
  window,
  onWindowChange,
  range,
  onRangeChange,
  custom,
  generatedAt,
}: {
  window: AbPresetWindow;
  onWindowChange: (next: AbPresetWindow) => void;
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
  custom: boolean;
  generatedAt: string | null;
}) {
  return (
    <header className="ab-tests__head">
      <div>
        <p className="ab-tests__sub">
          Both arms across the same funnel steps the Funnel page counts. Measured{" "}
          {generatedAt ? formatDateTime(generatedAt) : "-"}.
        </p>
      </div>
      <div className="ab-tests__controls">
        <div className="ab-tests__windows" role="group" aria-label="Window">
          {WINDOWS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              /* A preset is only ACTIVE while no range is set. Both lit at once
                 would claim the page is showing two different cohorts. */
              className={
                "ab-tests__window" +
                (!custom && entry.key === window ? " ab-tests__window--on" : "")
              }
              aria-pressed={!custom && entry.key === window}
              /* Picking a preset CLEARS the range: the two are answers to the
                 same question, and the range is the one that wins while set. */
              onClick={() => {
                onRangeChange(EMPTY_DATE_RANGE);
                onWindowChange(entry.key);
              }}
            >
              {entry.label}
            </button>
          ))}
        </div>
        {/* Cohorts by the day a client MET the experiment, which is exactly what
            the presets beside it do, so the two lanes cannot disagree about who
            is in a cohort. Not signup day: somebody first shown the rail today
            who joined in June belongs in "7d", and cutting on signup would drop
            them from the denominator while their clicks stayed in the numerator.
            Picking dates moves the page onto the on-demand lane, where the
            readout is computed rather than read. */}
        <DateRangeFilter label="Exposure dates" value={range} onChange={onRangeChange} />
      </div>
    </header>
  );
}

/** The two lanes behind one envelope. The four presets read a worker-materialized
 *  snapshot; a range is computed on demand. Only one of the two reads is ever
 *  live (the other is disabled, so no request is made for it), and both answer
 *  the same envelope, so everything downstream renders one shape. */
function useAbReadout(experiment: string | undefined, window: AbPresetWindow, range: DateRange) {
  const custom = isDateRangeActive(range);
  const preset = useQuery({ ...abTestsQuery(experiment, window), enabled: !custom });
  const ranged = useQuery({ ...abTestsRangeQuery(experiment, range), enabled: custom });
  const readout = custom ? ranged : preset;
  // How many times we have asked and been told `computing`. It lives on the query
  // STATE rather than in React state, for the reason the Funnel gives: the poll
  // owns the cadence, and a second counter would be a second, drifting answer to
  // "how long have we been waiting". Read from whichever lane is live: they are
  // separate cache entries and a range's wait says nothing about a preset's.
  const client = useQueryClient();
  const polls =
    client.getQueryState(
      custom
        ? adminKeys.abTestsRange(experiment ?? "", dateRangeKey(range))
        : adminKeys.abTests(experiment ?? "", window),
    )?.dataUpdateCount ?? 0;
  return { readout, custom, polls };
}

/** `computing` is not a failure: nothing was cached, the route enqueued the
 *  rebuild, and the query is polling for it. Say that, rather than showing an
 *  error for work that is actively happening. */
function ReadoutPending({
  gaveUp,
  custom,
  onRetry,
}: {
  gaveUp: boolean;
  custom: boolean;
  onRetry: () => void;
}) {
  return (
    <EmptyState
      title={gaveUp ? "Still building the readout" : "Building the readout"}
      message={
        gaveUp
          ? "The rebuild is taking longer than expected. It reads every user and " +
            "splits them into arms, so a large population takes a while; try again " +
            "in a moment, or pick a preset window."
          : custom
            ? "These dates have not been measured yet, so this asked the worker for " +
              "them. It reads every user and splits them into arms, so it lands in a " +
              "few seconds and this page is watching for it."
            : "Nothing was cached, so this asked the worker to measure both arms. It " +
              "lands in a few seconds and this page is watching for it."
      }
      action={{ label: "Check again", onClick: onRetry }}
    />
  );
}

/** One experiment's readout, from the evidence notice down: the arm cards, the
 *  shared funnel, what it consumed, the estimate, its own KPIs. Everything a
 *  reader needs to answer "did it move the numbers", with nothing about WHICH
 *  experiment, which the page around it decides. */
function ReadoutBody({ data }: { data: AdminAbTestsResponse }) {
  const selected = data.experiments.find((e) => e.name === data.selected);
  const dark = selected?.pct === 0;
  // The arms, baseline FIRST, from the readout (the registry's order); a
  // readout written before the list existed reads as today's two, with the
  // flat two-arm fields as its populations.
  const arms: readonly string[] = data.arms?.length ? data.arms : DEFAULT_ARMS;
  const usersOf = (arm: string, i: number): number =>
    data.armsUsers?.[arm] ?? (i === 0 ? data.controlUsers : data.experimentUsers);
  const guestsOf = (arm: string, i: number): number =>
    data.armsGuests?.[arm] ?? (i === 0 ? data.controlGuests : data.experimentGuests);
  const thin = arms.some((arm, i) => usersOf(arm, i) < THIN_ARM);

  return (
    <>
      {selected ? <p className="ab-tests__blurb">{selected.blurb}</p> : null}

      {/* The evidence notice, above the numbers rather than under them: a
            reader who scrolls past it has already read the table. */}
      {dark ? (
        <p className="ab-tests__warn">
          This experiment is dialled to <strong>0%</strong>, so nobody new is assigned to the
          experiment arm except testers who pinned themselves through the Debug flip. The split
          below is a smoke test that the plumbing works, not a result. Raise the allocation above to
          start collecting one.
        </p>
      ) : thin ? (
        <p className="ab-tests__warn">
          One of these arms holds fewer than {THIN_ARM} users. Read the gap as a direction to watch,
          not as a finding.
        </p>
      ) : null}

      {/* WHAT THESE NUMBERS COUNT, said before they are read. The arms hold the
            clients who actually reached this experiment, not everyone who hashes
            into a bucket, so they are far smaller than the user base and that is
            correct. Worth stating plainly: the old page counted every user who
            had ever signed up, which diluted every rate below by an amount
            nothing on screen disclosed. */}
      <p className="ab-tests__section-sub">
        Both arms count only clients who actually reached this experiment: for the rail, a turn
        where the agent asked for a talent preview; for the waiting dashboard, a concierge run
        starting. A client who never got that far is in neither arm. The steps themselves count
        whenever they happened, which for these experiments is after the client met them.
      </p>

      {/* One card per ARM, baseline first: N arms, N cards. */}
      <div className="ab-tests__arms">
        {arms.map((arm, i) => (
          <div
            key={arm}
            className={"ab-tests__arm" + (i > 0 ? ` ab-tests__arm--${armSuffix(i)}` : "")}
          >
            <span className="ab-tests__arm-label">{armLabel(arm, arms)}</span>
            <span className="ab-tests__arm-count">{usersOf(arm, i).toLocaleString()}</span>
            <span className="ab-tests__arm-sub">
              users, {guestsOf(arm, i).toLocaleString()} of them guests
            </span>
          </div>
        ))}
      </div>

      {/* THE SHARED FUNNEL. The same seven rungs on every experiment, which is
            what lets two experiments be read against each other at all. */}
      <section className="ab-tests__section">
        <h2 className="ab-tests__section-title">The product funnel</h2>
        <p className="ab-tests__section-sub">
          The same steps the Funnel and Version Cohorts pages count, so this page cannot disagree
          with them about what a step means.
        </p>
        <div className="ab-tests__pair">
          <RowTable
            caption="Share of each arm that reached the step. Signed up is the denominator, so it is not a row."
            rows={data.steps}
            arms={arms}
          />
          <div className="ab-tests__chart-wrap">
            <ArmBars
              rows={data.steps}
              arms={arms}
              label={`Share of each arm reaching each funnel step, every arm against the baseline, for ${selected?.label ?? data.selected}`}
            />
            <ChartKey arms={arms} />
          </div>
        </div>
      </section>

      {/* WHAT IT CONSUMED. Sits between the funnel (did it work) and the
            estimate below (what it may have cost in briefs), because it is the
            measured half of the same cost question the estimate models. */}
      {data.amounts.length > 0 ? (
        <section className="ab-tests__section">
          <h2 className="ab-tests__section-title">Searches and spend</h2>
          <p className="ab-tests__section-sub">
            How much work each arm actually consumed. Searches are counted off the same table the
            "Search ran" step above tests, so a client cannot read as having searched here and not
            there. Search agent spend is Scout's own model calls; all agent spend is every agent's,
            for these clients.
          </p>
          <div className="ab-tests__pair">
            <AmountTable rows={data.amounts} arms={arms} />
          </div>
        </section>
      ) : null}

      {data.cannibalization ? <CannibalizationPanel data={data.cannibalization} /> : null}

      {/* THIS EXPERIMENT'S OWN MEASUREMENTS. Absent for an experiment that
            registers none, and said out loud rather than drawn as an empty
            table: "no KPIs" and "no data" are different facts. */}
      <section className="ab-tests__section">
        <h2 className="ab-tests__section-title">
          {selected?.label ?? "This experiment"}: its own numbers
        </h2>
        {data.kpis.length === 0 ? (
          <p className="ab-tests__section-sub">
            This experiment registers no measurements of its own, so it is judged on the shared
            funnel above. Add them to its entry in <code>recruiter/experiments.py</code> to report
            the clicks that belong to it.
          </p>
        ) : (
          <>
            <p className="ab-tests__section-sub">
              The clicks that exist only inside this experiment, counted the same way: the share of
              each arm who ever did it. These rows mean nothing on another experiment's page.
            </p>
            <div className="ab-tests__pair">
              <RowTable
                caption="Share of each arm who performed the action at least once."
                rows={data.kpis}
                arms={arms}
              />
              <div className="ab-tests__chart-wrap">
                <ArmBars
                  rows={data.kpis}
                  arms={arms}
                  label={`Share of each arm performing each ${selected?.label ?? "experiment"} action, every arm against the baseline`}
                />
                <ChartKey arms={arms} />
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** ONE product experiment's outcomes, as the Outcomes tab of
 *  `/experiments/$key` renders them. The experiment is fixed by the page, so the
 *  only controls here are the cohort window and the exposure-date range, held
 *  locally: a tab inside a detail page is not its own bookmarkable board. */
/** The PLAYGROUND: the A/B readout as a page of its own, with a tab per
 *  registered experiment.
 *
 *  RESTORED 2026-09-15. #1231 deleted this page when the readout became the Outcomes
 *  tab of each experiment (`ExperimentOutcomes` below), and `/ab-tests` became a
 *  redirect onto the experiments register. That was right about the duplication and
 *  wrong about what the nav promises: the sidebar still says "Playground", and an
 *  operator who clicks it to compare two arms across the funnel landed on a register
 *  of ninety-six experiments instead. #1231 removed the System Admin dial card the
 *  same way and that was restored on 2026-09-11; this is the other half.
 *
 *  It is the SAME readout the Outcomes tab renders -- one `useAbReadout`, one
 *  `ReadoutBody` -- so the two surfaces cannot disagree about a number. What this adds
 *  is the part the tab cannot have: the experiment is CHOSEN here rather than fixed by
 *  the route, and every registered experiment is visible at once with its live dial, so
 *  a reader can see how many exist and which are dark without opening each one.
 *
 *  State lives on the ROUTE (`?experiment=`), not here, so a filtered readout is a link
 *  somebody can be sent -- which is why the props are passed in rather than held. */
export function AbTestsPage({
  experiment,
  onExperimentChange,
  window,
  onWindowChange,
  range,
  onRangeChange,
}: {
  experiment: string | undefined;
  onExperimentChange: (next: string) => void;
  window: AbPresetWindow;
  onWindowChange: (next: AbPresetWindow) => void;
  /** The operator-chosen cohort range. EMPTY = use the preset above, which is
   *  the page's default and its only cheap lane. */
  range: DateRange;
  onRangeChange: (next: DateRange) => void;
}) {
  const { readout, custom, polls } = useAbReadout(experiment, window, range);

  if (readout.isLoading && !readout.data) return <LoadingState label="Reading the arms" />;
  if (readout.isError) {
    return (
      <AreaBoundary area="ab_tests" error={readout.error} onRetry={() => void readout.refetch()} />
    );
  }
  const data = readout.data?.readout ?? null;
  return (
    <div className="ab-tests">
      <CohortTabs active="ab" />
      <AbControls
        window={window}
        onWindowChange={onWindowChange}
        range={range}
        onRangeChange={onRangeChange}
        custom={custom}
        generatedAt={data?.generatedAt || null}
      />
      {!data ? (
        <ReadoutPending
          gaveUp={funnelRangeGaveUp(readout.data?.status, polls)}
          custom={custom}
          onRetry={() => void readout.refetch()}
        />
      ) : (
        <>
          {/* One tab per REGISTERED experiment, each carrying its live dial, so a
              dark experiment reads as dark where the reader CHOOSES it rather than
              only in the notice further down. A dropdown hid how many exist. */}
          <div className="ab-tests__tabs" role="tablist" aria-label="Experiment">
            {data.experiments.map((entry) => {
              const on = entry.name === data.selected;
              return (
                <button
                  key={entry.name}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={"ab-tests__tab" + (on ? " ab-tests__tab--on" : "")}
                  onClick={() => onExperimentChange(entry.name)}
                >
                  <span className="ab-tests__tab-label">{entry.label}</span>
                  <span
                    className={
                      "ab-tests__tab-dial" + (entry.pct === 0 ? " ab-tests__tab-dial--dark" : "")
                    }
                  >
                    {entry.pct}% dialled
                  </span>
                </button>
              );
            })}
          </div>
          <ReadoutBody data={data} />
        </>
      )}
    </div>
  );
}

export function ExperimentOutcomes({ experiment }: { experiment: string }) {
  const [window, setWindow] = useState<AbPresetWindow>("all");
  const [range, setRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const { readout, custom, polls } = useAbReadout(experiment, window, range);

  if (readout.isLoading && !readout.data) return <LoadingState label="Reading the arms" />;
  if (readout.isError) {
    return (
      <AreaBoundary area="ab_tests" error={readout.error} onRetry={() => void readout.refetch()} />
    );
  }
  const data = readout.data?.readout ?? null;
  return (
    <div className="ab-tests ab-tests--embedded">
      <AbControls
        window={window}
        onWindowChange={setWindow}
        range={range}
        onRangeChange={setRange}
        custom={custom}
        generatedAt={data?.generatedAt || null}
      />
      {!data ? (
        <ReadoutPending
          gaveUp={funnelRangeGaveUp(readout.data?.status, polls)}
          custom={custom}
          onRetry={() => void readout.refetch()}
        />
      ) : data.selected !== experiment ? (
        // The route resolves an unknown name to the first registered experiment
        // rather than 404ing; on a page ABOUT one experiment that would print
        // another's numbers under this one's heading.
        <p className="ab-tests__warn">
          The A/B readout has no experiment named <code>{experiment}</code>, so there are no
          outcomes to show. It measures product experiments registered in{" "}
          <code>recruiter/experiments.py</code>.
        </p>
      ) : (
        <ReadoutBody data={data} />
      )}
    </div>
  );
}

/** Which bar is which. Sits under every chart rather than once on the page: a
 *  reader who scrolled to the second chart should not have to scroll back. */
function ChartKey({ arms }: { arms: readonly string[] }) {
  return (
    <p className="ab-tests__chart-key">
      {arms.map((arm, i) => (
        <span key={arm}>
          <span
            className={`ab-tests__chart-key-swatch ab-tests__chart-key-swatch--${armSuffix(i)}`}
          />
          {armLabel(arm, arms)}{" "}
        </span>
      ))}
      <span className="ab-tests__chart-key-note">
        No bar means that arm has no users to measure.
      </span>
    </p>
  );
}
